import React, { forwardRef, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Divider, FormLabel, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';
import { Form, Formik, getIn } from 'formik';
import InputField from './Input-Field/input-field';
import { deleteFileAttachment, downloadFileAttachment, getFilesByOrderId, updateFileAttachment, uploadFileAttachment } from 'services/order-service';
import ReactCardFlip from 'react-card-flip';
import { Toggle } from 'rsuite';
import ConfirmationDialog from './dialogs/ConfirmationDialog';
import ToastHandler from './Toast-Handler/toast-handler';

const LoadDocumentManager = forwardRef(({
  orderId,
  isOTR,
  documentList,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [files, setFiles] = useState(documentList);

  const [toast, setToast] = useState(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isEditDocument, SetIsEditDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [fileModal, setFileModal] = useState({
    file: null,
    fileName: '',
    fileDesc: '',
    orderId: '',
    invoiceAttachable: false,
    isOTR: '',
    type: '',
  });

  const fileInput = useRef(null);

  const handleDeleteDocument = () => {
    setIsProcessing(true);
    deleteFileAttachment(orderId, selectedDocument.uuid).then((res) => {
      if (res.status === 200) {
        setToast({type: 'success', message: 'Document deleted successfully.'});
      } else {
        setToast({type: 'error', message: 'Failed to delete document.'});
      }
    }).catch(() => setToast({type: 'error', message: 'Failed to delete document.'}))
      .finally(() => {
        loadFilesByOrder();
        setSelectedDocument(null);
    });
  };

  const handleEditDocument = (doc) => {    
    setFileModal({file: doc,
        fileName: doc.fileName,
        fileDesc: doc.fileDesc,
        orderId: doc.orderId,
        invoiceAttachable: doc.invoiceAttachable,
      });
    SetIsEditDocument(true);
    setIsFileSelected(true);
  };

  const handleDownloadDocument = (doc) => {
    downloadFileAttachment(doc.uuid).then((res) => {
      if (res.status === 200 && res.data?.ok) {
        window.open(res.data.link, '_blank', 'noreferrer');
      } else {
        setToast({ type: 'error', message: 'Failed to download the document.' });
      }
    }).catch(() => setToast({ type: 'error', message: 'Failed to download the document.' }));
  };

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f && f.size < 5 * 1024 * 1024) {
      setFileModal({ file: f,
        fileName: f.name 
      });
      setIsFileSelected(true);
    } else {
      setFileModal({});
      setToast({ type: 'error', message: 'Invalid File or File is too big.' });
    }
  };

  const loadFilesByOrder = () => {
    getFilesByOrderId(orderId, !!isOTR).then(res => {
      if (res.status === 200 && res.data) {
        setFiles(res.data);
      } else {
        setToast({type: 'error', message: 'Failed to get documents.'});
      }
    }).catch(e => setToast({type: 'error', message: 'Failed to get documents.' + e}))
      .finally(() => {
        setIsProcessing(false);
        setTimeout(() => {
          setToast({});
        }, 10000);
      });
  };

  const closeFileUploadForm = (e) => {
    loadFilesByOrder();
    setIsFileSelected(false); // closes the form
    SetIsEditDocument(false);
  };

  return (
    <Box
      gap={2}
    >
      <ReactCardFlip
        isFlipped={isFileSelected}
        flipDirection='vertical'
      >
        {/* Front */}
        <>
          <Box
            height={'auto'}
            border={`1px solid ${theme.palette.neutral[200]}`}
            borderRadius={'6px'}
            p={2}
            gap={1}
            backgroundColor={colors.base.white}
          >
            <Stack spacing={1} sx={{ alignItems: 'center' }}>
              <FTIcons.Folder
                width={40}
                height={30}
                fill={colors.main_blue[200]}
              />
              <Button
                variant='outlined'
                sx={{
                  background: colors.main_blue[50],
                  border: `1px solid ${colors.primary.light}`,
                  color: colors.primary.main
                }}
                startIcon={
                  <FTIcons.UploadSimple width={18} height={18} fill={colors.primary.main} />
                }
                onClick={() => fileInput.current && fileInput.current.click()}
              >
                Select Document
              </Button>
              <Typography variant='caption' color={colors.neutral[400]}>Supported format: PDF, Docs, Max file size: 5MB</Typography>
              <input type='file' onChange={handleFileInput} ref={fileInput} style={{ display: 'none' }} />
            </Stack>
          </Box>
          <Stack mt={2}>
            {files.map(doc => (
              <>
                <Box mt={1} key={doc.uuid} display={'flex'} justifyContent={'space-between'} sx={{wordBreak: 'break-word'}}>
                  <Typography variant={'body1'} fontWeight={500} color={colors.neutral[900]}>{doc.fileName}</Typography>
                  <Box>
                    <IconButton size='small' onClick={() => {setSelectedDocument(doc); setIsConfirmationDialogOpen(true); }}>
                      <FTIcons.RemoveIcon width={16} height={16} fill={colors.error.main} />
                    </IconButton>
                    <IconButton size='small' onClick={() => handleEditDocument(doc)}>
                      <FTIcons.EditIcon width={16} height={16} fill={colors.info.dark} />
                    </IconButton>
                    <IconButton size='small' onClick={() => handleDownloadDocument(doc)}>
                      <FTIcons.DownloadIcon width={16} height={16} fill={colors.success.dark} />
                    </IconButton>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent:'space-between'}}>
                  <Typography variant={'caption'} fontWeight={400} color={colors.neutral[700]}>{new Date(doc.createdDate).toDateString()}</Typography>
                  <Typography variant={'caption'} fontWeight={400} color={colors.neutral[700]} gutterBottom>{doc.fileDesc}</Typography>
                  <Typography variant={'caption'} fontWeight={400} color={colors.neutral[700]} gutterBottom>Attached to Invoice? {doc.invoiceAttachable ? 'Yes' : 'No'}</Typography>
                </Box>
                <Divider />
              </>
            ))}
          </Stack>
        </>
        <Formik
          initialValues={{ fileModal }}
          enableReinitialize={true}
          onSubmit={(values) => {
            const { file, fileDesc, invoiceAttachable } = values.fileModal;
            const formData = new FormData();
            if(isEditDocument) {
              formData.append('fileDesc', fileDesc);
              formData.append('invoiceAttachable', invoiceAttachable ? true : false);
              updateFileAttachment(formData, file.uuid).then(r => {
                if (r.status === 200 && r.data?.ok) {
                  setToast({type: 'success', message: 'File Uploaded successfully.'});
                } else {
                  setToast({type: 'error', message: 'Failed to upload the file.'});
                }
              }).catch(() => setToast({type: 'error', message: 'Failed to upload the file.'}))
                .finally(() => {
                  setIsProcessing(false);                                    
                });
            } else {
              formData.append('file', file);
              formData.append('orderId', orderId);
              formData.append('isOTR', !!isOTR);
              formData.append('fileDesc', fileDesc);
              formData.append('invoiceAttachable', invoiceAttachable ? true : false);
              setIsProcessing(true);
              uploadFileAttachment(formData).then(r => {
                if (r.status === 200 && r.data?.ok) {
                  setToast({type: 'success', message: 'File Uploaded successfully.'});
                } else {
                  setToast({type: 'error', message: 'Failed to upload the file.'});
                }
              }).catch(() => setToast({type: 'error', message: 'Failed to upload the file.'}))
                .finally(() => {
                  setIsProcessing(false);
                  setFileModal({});
                });
            }}
          }
        >
          {({
            errors,
            handleBlur,
            handleChange,
            touched,
            values,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12 }}>
                  {isProcessing && <Box textAlign={'center'}><CircularProgress /></Box>}
                  {toast && <ToastHandler toast={toast} type={toast.type} />}
                </Grid>
                <Grid size={{ xs: 12}}>
                  <Box display={'flex'} gap={2}>
                    <InputField
                      type='text'
                      label='File Name'
                      name='fileModal.fileName'
                      value={values.fileModal.fileName}
                      variant='outlined'
                      fullWidth
                      error={Boolean(
                        getIn(touched, 'fileModal.fileName') &&
                        getIn(errors, 'fileModal.fileName')
                      )}
                      helperText={Boolean(
                        getIn(touched, 'fileModal.fileName') &&
                        getIn(errors, 'fileModal.fileName')
                      )}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      margin='dense'
                      size='small'
                    />
                    {fileModal && fileModal.file && <FormLabel component='legend'>Size: {(fileModal.file.size / 1024).toFixed(2) + ' KB'}</FormLabel>}
                  </Box>
                </Grid>                
                <Grid size={{ xs: 12, sm: 12 }}>
                  <InputField
                    type='text'
                    label='File Description'
                    name='fileModal.fileDesc'
                    value={values.fileModal.fileDesc}
                    variant='outlined'
                    fullWidth
                    error={Boolean(
                      getIn(touched, 'fileModal.fileDesc') &&
                      getIn(errors, 'fileModal.fileDesc')
                    )}
                    helperText={Boolean(
                      getIn(touched, 'fileModal.fileDesc') &&
                      getIn(errors, 'fileModal.fileDesc')
                    )}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    margin='dense'
                    size='small'
                    multiline
                  />
                </Grid>
                <Grid sx={{ xs: 12, sm: 4 }}>
                  <Box display={'flex'} gap={2} alignItems={'center'}>
                    <FormLabel component='legend'>Attach to Invoice</FormLabel>
                    <Typography variant='body1'>No</Typography>
                    <Toggle
                      name='fileModal.invoiceAttachable'
                      value={values.fileModal.invoiceAttachable}
                      checkedChildren={true}
                      unCheckedChildren={false}
                      defaultChecked={values.fileModal.invoiceAttachable}
                      onChange={(val) => setFieldValue('fileModal.invoiceAttachable', val)}
                      fullWidth={true}
                    />
                    <Typography variant='body1'>Yes</Typography>   
                  </Box>
                </Grid>
              </Grid>
              <Box
                display='flex'
                justifyContent='flex-end'
                p={1}>
                <Button
                  variant='text'
                  color='secondary'
                  onClick={closeFileUploadForm}
                >
                  Close
                </Button>

                <Button
                  type='submit'
                  variant='contained'
                  size='small'
                  sx={{
                    background: colors.main_blue[50],
                    border: `1px solid ${colors.primary.light}`,
                    color: colors.primary.main
                  }}
                >
                  Upload
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </ReactCardFlip>
      {isConfirmationDialogOpen && <ConfirmationDialog
        open={isConfirmationDialogOpen}
        title={'Delete Document'}
        isProcessing={isProcessing}
        handleConfirm={() => handleDeleteDocument()}
        handleCancel={() => setIsConfirmationDialogOpen(false)}
        handleCloseFunc={() => setIsConfirmationDialogOpen(false)}
      >
        {toast && <ToastHandler toast={toast} type={toast.type} />}
        {selectedDocument && <Typography variant='subtitle1'>{`Do you want to delete ${selectedDocument.fileName} ?`}</Typography> }
      </ConfirmationDialog>}
    </Box>
  )
});

export default LoadDocumentManager;